import { Tooltip } from "antd";
import type { PlayerCardData, PlayerCardInjury, PlayerCardStatusEntry } from "../Processing/types";
import {
  injuryName,
  formatActiveAge,
  injuryRangeParts,
  sortAvailabilityEntries,
} from "../Utils/playerCardFormat";
import { useHeadshot } from "@hooks/useHeadshot";
import "@styles/styles-PlayerCard.css";

const ACTIVE_CAP = 4;

export function PlayerCardView({ data }: { data: PlayerCardData }) {
  const headshotSrc = useHeadshot(data);
  const showPhoto = data.headshot !== undefined || data.headshot_image_url !== undefined;
  const showName = data.name !== undefined;
  const showPos = data.position !== undefined;
  const showJersey = data.jersey !== undefined;
  const showIdentity = showPhoto || showName || showPos || showJersey;

  const showStats =
    data.top_speed !== undefined ||
    data.bodyweight_baseline !== undefined ||
    data.last_5_status !== undefined;
  const showActive = data.active_injuries !== undefined;
  const showRecent = data.last_5_injuries !== undefined;

  if (!showIdentity && !showStats && !showActive && !showRecent) {
    return (
      <div className="player-card">
        <div className="pc-empty">No data</div>
      </div>
    );
  }

  const sub = [showPos ? data.position : null, showJersey ? `#${data.jersey}` : null]
    .filter(Boolean)
    .join(" · ");

  return (
    <div className="player-card">
      {showIdentity && (
        <div className="pc-head">
          {showPhoto && (
            <div className="pc-photo">
              {headshotSrc ? (
                <img src={headshotSrc} alt={data.name ?? "Player"} />
              ) : (
                <Silhouette />
              )}
            </div>
          )}
          {(showName || sub) && (
            <div className="pc-id">
              {showName && <div className="pc-name">{data.name}</div>}
              {sub && <div className="pc-sub">{sub}</div>}
            </div>
          )}
        </div>
      )}

      {(showStats || showActive || showRecent) && (
        <div className="pc-body">
          {showStats && (
            <div className="pc-section">
              {data.top_speed !== undefined && <StatRow label="Top Speed" value={data.top_speed} />}
              {data.bodyweight_baseline !== undefined && (
                <StatRow label="Body weight Baseline" value={data.bodyweight_baseline} />
              )}
              {data.last_5_status !== undefined && <AvailabilityRow last5={data.last_5_status} />}
            </div>
          )}
          {data.active_injuries !== undefined && <ActiveInjuries injuries={data.active_injuries} />}
          {data.last_5_injuries !== undefined && <RecentInjuries injuries={data.last_5_injuries} />}
        </div>
      )}
    </div>
  );
}

function Silhouette() {
  return (
    <svg className="pc-silhouette" viewBox="0 0 24 24" fill="#8a99ab" aria-hidden="true">
      <path d="M12 12c2.7 0 4.8-2.1 4.8-4.8S14.7 2.4 12 2.4 7.2 4.5 7.2 7.2 9.3 12 12 12Zm0 2.4c-3.2 0-9.6 1.6-9.6 4.8v2.4h19.2v-2.4c0-3.2-6.4-4.8-9.6-4.8Z" />
    </svg>
  );
}

function StatRow({ label, value }: { label: string; value: number }) {
  return (
    <div className="pc-row">
      <span className="pc-row-key">{label}</span>
      <span className="pc-row-value">{value}</span>
    </div>
  );
}

function AvailabilityRow({ last5 }: { last5: Record<string, PlayerCardStatusEntry> }) {
  const cells = sortAvailabilityEntries(last5);
  return (
    <div className="pc-row pc-avail-row">
      <span className="pc-row-key">Recent Avail.</span>
      <span className="pc-avail">
        {cells.map((c) => (
          <Tooltip
            key={c.date}
            placement="top"
            title={
              <div style={{ display: "flex", flexDirection: "column" }}>
                <span><strong>Date: </strong>{c.label}</span>
                <span><strong>Status: </strong>{c.name}</span>
              </div>
            }
          >
            <span className="pc-dot" style={{ background: c.color }} />
          </Tooltip>
        ))}
      </span>
    </div>
  );
}

function ActiveInjuries({ injuries }: { injuries: PlayerCardInjury[] }) {
  return (
    <div className="pc-section">
      <div className="pc-sec-label">Active Injuries</div>
      {injuries.length === 0 ? (
        <div className="pc-none">
          <span className="pc-none-check">✓</span>No active injuries
        </div>
      ) : (
        <>
          {injuries.slice(0, ACTIVE_CAP).map((inj) => (
            <div className="pc-row" key={inj.injury_id}>
              <span className="pc-row-name">{injuryName(inj)}</span>
              <span className="pc-row-meta">{formatActiveAge(inj)}</span>
            </div>
          ))}
          {injuries.length > ACTIVE_CAP && (
            <div className="pc-more">
              <Tooltip
                placement="right"
                title={
                  <div style={{ display: "flex", flexDirection: "column" }}>
                    {injuries.slice(ACTIVE_CAP).map((inj) => (
                      <span key={inj.injury_id}>
                        {injuryName(inj)} · {formatActiveAge(inj)}
                      </span>
                    ))}
                  </div>
                }
              >
                <span>+{injuries.length - ACTIVE_CAP} more</span>
              </Tooltip>
            </div>
          )}
        </>
      )}
    </div>
  );
}

function RecentInjuries({ injuries }: { injuries: PlayerCardInjury[] }) {
  return (
    <div className="pc-section">
      <div className="pc-sec-label">Recent Injuries</div>
      {injuries.length === 0 ? (
        <div className="pc-none-neutral">None recorded</div>
      ) : (
        injuries.map((inj) => {
          const { start, end } = injuryRangeParts(inj);
          return (
            <div className="pc-row" key={inj.injury_id}>
              <span className="pc-row-name">{injuryName(inj)}</span>
              <span className="pc-row-meta pc-injury-range">
                <span className="pc-range-date">{start}</span>
                <span className="pc-range-sep">–</span>
                <span className="pc-range-date">{end}</span>
              </span>
            </div>
          );
        })
      )}
    </div>
  );
}
